import React, { useContext, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Badge, Button, Collapse, ListItem } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ChevronDown as ExpandMoreIcon, ChevronUp as ExpandLessIcon } from 'lucide-react';
import i18n from 'i18next';
import { TopMenuOptionContext } from 'src/context/TopMenuOptionContext';

const useStyles = makeStyles(theme => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0,
    color: '#FFF'
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
    color: '#FFF'
  },
  button: {
    color: '#FFF',
    padding: '10px 12px',
    borderRadius: 8,
    marginBottom: 3,
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' },
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    minHeight: 38,
    '& svg': { flexShrink: 0 }
  },
  buttonLeaf: {
    color: '#FFF',
    padding: '10px 12px',
    borderRadius: 8,
    marginBottom: 3,
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' },
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    minHeight: 38,
    '& svg': { flexShrink: 0 },
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      '& $title': {
        fontWeight: theme.typography.fontWeightMedium
      }
    }
  },
  buttonLeafTwo: {
    color: '#FFF',
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    minWidth: 20,
    flex: '0 0 20px',
    marginRight: theme.spacing(1)
  },
  chevron: {
    width: 18,
    height: 18,
    flexShrink: 0,
    marginLeft: theme.spacing(1)
  },
  title: {
    marginRight: 'auto',
    color: '#FFF',
    minWidth: 0,
    textAlign: 'left',
    lineHeight: 1.4
  },
  active: {
    backgroundColor: 'rgba(255, 255, 255, 0.19)',
    borderLeft: '3px solid #ffffff',
    '& $title': {
      fontWeight: theme.typography.fontWeightMedium,
      color: '#FFF'
    },
    '& $icon': {
      color: '#FFF'
    }
  }
}));

function NavItem({
  title,
  href,
  depth,
  children,
  icon: Icon,
  className,
  open: openProp,
  info: Info,
  pathName,
  gpsNotifications,
  ...rest
}) {
  const classes = useStyles();
  const { setMenuOption } = useContext(TopMenuOptionContext);
  const [open, setOpen] = useState(openProp);
  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = { paddingLeft };

  const getTitleMenuOption = titleMenu => {
    setMenuOption({ value: titleMenu });
  };

  if (title === 'loanPerformanceDashboard') {
    return null;
  }

  if (children) {
    return (
      <ListItem
        key={title}
        className={clsx(classes.item, className)}
        disableGutters
        {...rest}
      >
        <Button
          className={classes.button}
          color="secondary"
          onClick={handleToggle}
          style={style}
          variant="contained"
        >
          {Icon && <span className={classes.icon}><Icon size={20} strokeWidth={1.8} /></span>}

          {title === 'riskManagement' || title === 'collateral' ? (
            <Badge
              badgeContent={gpsNotifications}
              className={classes.title}
              color="error"
              overlap="rectangular"
            >
              <span>{i18n.t(title)}</span>
            </Badge>
          ) : (
            <span className={classes.title}>{i18n.t(title)}</span>
          )}

          {open ? (
            <ExpandLessIcon className={classes.chevron} size={18} />
          ) : (
            <ExpandMoreIcon className={classes.chevron} size={18} />
          )}
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  return (
    <ListItem
      key={title}
      className={clsx(classes.itemLeaf, className)}
      disableGutters
      {...rest}
    >
      {title === 'loanPerformanceDashboard' ? (
        <Button
          className={classes.buttonLeafTwo}
          color="secondary"
          exact
          variant="contained"
        >
          <a
            href="https://servicing-portfolio-management.prestti.com/"
            rel="noopener noreferrer"
            style={{
              textDecoration: 'none',
              width: '100%',
              fontSize: 12,
              marginLeft: 25
            }}
            target="_blank"
          >
            <span className={classes.title}>{i18n.t(title)}</span>
          </a>
        </Button>
      ) : (
        <Button
          className={clsx(
            classes.buttonLeaf,
            `depth-${depth}`,
            pathName.includes(href) && classes.active
          )}
          color="secondary"
          component={RouterLink}
          exact
          onClick={() => getTitleMenuOption(title)}
          style={style}
          to={href}
          variant="contained"
        >
          {Icon && <span className={classes.icon}><Icon size={20} strokeWidth={1.8} /></span>}

          {title === 'gpsMonitoring' ? (
            <Badge
              badgeContent={gpsNotifications}
              className={classes.title}
              color="error"
              overlap="rectangular"
            >
              <span>{i18n.t(title)}</span>
            </Badge>
          ) : (
            <span className={classes.title}>{i18n.t(title)}</span>
          )}

          {Info && <Info className={classes.info} />}
        </Button>
      )}
    </ListItem>
  );
}

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.any,
  info: PropTypes.any,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  pathName: PropTypes.string,
  gpsNotifications: PropTypes.number
};

NavItem.defaultProps = {
  open: false
};

export default NavItem;
